import { useMemo } from "react";
import { styled } from "@pigment-css/react";
import { useTranslations } from "@acto/web-common/locales";
import { SingleSelect, InfiniteScrollSelect } from "../../../primitives";
import { useManagedEmployees } from "@acto/taskpane/core/data-access/team/useManagedEmployees";
import { useDebouncedSearchTerm } from "@acto/taskpane/core/utils/hooks/useDebouncedSearchTerm";

export interface WorkflowConfigurationValues {
  repeat: boolean;
  dueDate: string;
  pauseTill?: string;
  fixedResponsibleEmployeeId?: string;
}

export interface ResponsibleEmployeeOption {
  id: string;
  name: string;
}

interface ConfigurationSectionProps {
  configuration: WorkflowConfigurationValues;
  onConfigurationChange: (config: WorkflowConfigurationValues) => void;
  editingEmployee?: ResponsibleEmployeeOption;
}

const Container = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.managementView.spacing[12],
}));

const Row = styled("div")(({ theme }) => ({
  display: "flex",
  gap: theme.managementView.spacing[24],
  alignItems: "flex-start",
}));

const Field = styled("div")(({ theme }) => ({
  display: "flex",
  flex: "1 0 0",
  flexDirection: "column",
  gap: theme.managementView.spacing[12],
}));

// Repeat field is capped at half the panel width
const RepeatField = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.managementView.spacing[12],
  maxWidth: "50%",
}));

const Label = styled("label")(({ theme }) => ({
  fontFamily: theme.managementView.font.familySecondary,
  fontSize: theme.managementView.font.size[14],
  fontWeight: theme.managementView.font.weight[440],
  lineHeight: theme.managementView.font.lineHeight[164],
  color: theme.managementView.colors.neutral[56],
}));

const InputGroup = styled("div")(({ theme }) => ({
  display: "flex",
  height: "48px",
  alignItems: "center",
  backgroundColor: theme.managementView.colors.neutral[0],
  border: `1px solid ${theme.managementView.colors.neutral[8]}`,
  borderRadius: theme.managementView.radius[16],
  padding: "1px",
}));

const NumberInput = styled("input")(({ theme }) => ({
  fontFamily: theme.managementView.font.familySecondary,
  fontSize: theme.managementView.font.size[15],
  fontWeight: theme.managementView.font.weight[440],
  lineHeight: theme.managementView.font.lineHeight[164],
  letterSpacing: theme.managementView.font.letterSpacing.tight,
  color: theme.managementView.colors.neutral[56],
  backgroundColor: "transparent",
  border: "none",
  borderRight: `1px solid ${theme.managementView.colors.neutral[8]}`,
  outline: "none",
  width: "56px",
  flexShrink: 0,
  padding: `0 ${theme.managementView.spacing[16]}`,
  appearance: "textfield",

  "&::-webkit-outer-spin-button, &::-webkit-inner-spin-button": {
    appearance: "none",
    margin: 0,
  },

  "&:focus": {
    color: theme.managementView.colors.neutral[90],
  },
}));

const UNIT_SUFFIXES: Record<string, string> = {
  day: "d",
  week: "w",
  month: "m",
  year: "y",
};

const SUFFIX_TO_UNIT: Record<string, string> = Object.fromEntries(
  Object.entries(UNIT_SUFFIXES).map(([k, v]) => [v, k]),
);

const parseDueDate = (value: string): { amount: number; unit: string } => {
  const match = value.match(/^(\d+)([dwmy])$/);
  if (match) {
    return { amount: parseInt(match[1], 10), unit: SUFFIX_TO_UNIT[match[2]] ?? "month" };
  }
  return { amount: 1, unit: "month" };
};

const formatDueDate = (amount: number, unit: string): string => {
  return `${amount}${UNIT_SUFFIXES[unit] ?? "m"}`;
};

export const ConfigurationSection = ({
  configuration,
  onConfigurationChange,
  editingEmployee,
}: ConfigurationSectionProps) => {
  const t = useTranslations("managementView.workflow.createWorkflow");

  const { setDisplayValue: setSearchValue, debouncedValue: debouncedSearchTerm } = useDebouncedSearchTerm();

  const {
    data: employees = [],
    isPending,
    isFetching,
    hasNextPage,
    fetchNextPage,
  } = useManagedEmployees({
    searchTerm: debouncedSearchTerm || undefined,
  });

  const employeeOptions = useMemo(() => {
    const notSelectedOption = { id: "", name: t("config.notSelected") };
    const list = [notSelectedOption, ...employees];

    if (editingEmployee && !employees.some((e) => e.id === editingEmployee.id)) {
      list.splice(1, 0, editingEmployee);
    }

    return list;
  }, [employees, editingEmployee, t]);

  const pauseTillConfig = configuration.pauseTill
    ? parseDueDate(configuration.pauseTill)
    : { amount: 1, unit: "month" };
  const dueDateConfig = parseDueDate(configuration.dueDate);
  const isDueDateSameDay = dueDateConfig.amount === 0;

  const repeatOptions = [
    { key: "yes", label: t("config.yes") },
    { key: "never", label: t("config.never") },
  ];

  const timeUnitOptions = [
    { key: "day", label: t("config.timeUnits.day") },
    { key: "week", label: t("config.timeUnits.week") },
    { key: "month", label: t("config.timeUnits.month") },
    { key: "year", label: t("config.timeUnits.year") },
  ];

  const dueDateModeOptions = [
    { key: "sameDay", label: t("config.dueDateMode.sameDay") },
    { key: "custom", label: t("config.dueDateMode.custom") },
  ];

  return (
    <Container>
      <RepeatField>
        <Label>{t("config.repeat")}</Label>
        <InputGroup>
          <SingleSelect
            options={repeatOptions}
            value={configuration.repeat ? "yes" : "never"}
            onChange={(v) => {
              const newRepeat = v !== "never";
              onConfigurationChange({
                ...configuration,
                repeat: newRepeat,
                ...(newRepeat && !configuration.pauseTill ? { pauseTill: "1m" } : {}),
              });
            }}
          />
        </InputGroup>
      </RepeatField>

      {configuration.repeat && (
        <Row>
          <Field>
            <Label>{t("config.pauseTill")}</Label>
            <InputGroup>
              <NumberInput
                type="number"
                value={pauseTillConfig.amount}
                onChange={(e) => {
                  const amount = parseInt(e.target.value, 10) || 1;
                  onConfigurationChange({
                    ...configuration,
                    pauseTill: formatDueDate(amount, pauseTillConfig.unit),
                  });
                }}
                min="1"
              />
              <SingleSelect
                options={timeUnitOptions}
                value={pauseTillConfig.unit}
                onChange={(v) =>
                  onConfigurationChange({
                    ...configuration,
                    pauseTill: formatDueDate(pauseTillConfig.amount, v),
                  })
                }
              />
            </InputGroup>
          </Field>
        </Row>
      )}

      <Field>
        <Label>{t("config.dueDate")}</Label>
        <InputGroup>
          <SingleSelect
            options={dueDateModeOptions}
            value={isDueDateSameDay ? "sameDay" : "custom"}
            onChange={(v) => {
              onConfigurationChange({
                ...configuration,
                dueDate: v === "sameDay" ? "0d" : "1m",
              });
            }}
          />
        </InputGroup>
        {!isDueDateSameDay && (
          <InputGroup>
            <NumberInput
              type="number"
              value={dueDateConfig.amount}
              onChange={(e) => {
                const amount = parseInt(e.target.value, 10) || 1;
                onConfigurationChange({
                  ...configuration,
                  dueDate: formatDueDate(amount, dueDateConfig.unit),
                });
              }}
              min="1"
            />
            <SingleSelect
              options={timeUnitOptions}
              value={dueDateConfig.unit}
              onChange={(v) =>
                onConfigurationChange({
                  ...configuration,
                  dueDate: formatDueDate(dueDateConfig.amount, v),
                })
              }
            />
          </InputGroup>
        )}
      </Field>

      <Field>
        <Label>{t("config.selectedEmployeeTitle")}</Label>
        <InputGroup>
          <InfiniteScrollSelect
            options={employeeOptions}
            value={configuration.fixedResponsibleEmployeeId ?? ""}
            isLoading={isPending || isFetching}
            onSearch={setSearchValue}
            onLoadMore={fetchNextPage}
            hasMore={hasNextPage}
            onChange={(value) =>
              onConfigurationChange({
                ...configuration,
                fixedResponsibleEmployeeId: value || undefined,
              })
            }
          />
        </InputGroup>
      </Field>
    </Container>
  );
};
